"use client"

import { Sparkles, CheckCircle2, Bug, TrendingUp } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { ScrollArea } from "../ui/scroll-area"
import { Separator } from "../ui/separator"
import Image from "next/image"
import { useTheme } from "next-themes"
import Link from "next/link"
import type { ReleaseNote } from "../lib/data/release-notes"

interface ReleaseNotesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  releaseNote: ReleaseNote
  hideViewAllLink?: boolean
}

export function ReleaseNotesDialog({ open, onOpenChange, releaseNote, hideViewAllLink = false }: ReleaseNotesDialogProps) {
  const { theme, systemTheme } = useTheme()
  const resolvedTheme = theme === "system" ? systemTheme : theme
  const isDark = resolvedTheme === "dark"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] flex flex-col gap-0 p-0">
        <DialogHeader className="p-6 pb-4 shrink-0">
          <DialogTitle className="sr-only">What's New in AskFinz?</DialogTitle>
          <DialogDescription className="sr-only">
            Learn about the latest features, improvements, and bug fixes in AskFinz version {releaseNote.version}.
          </DialogDescription>
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Image
                  src={isDark ? "/shared/logos/header/askFinz_white.svg" : "/shared/logos/header/askFinz_black.svg"}
                  alt="AskFinz Logo"
                  width={120}
                  height={32}
                  className="h-8 w-auto"
                  priority
                />
                <span className="text-2xl font-semibold tracking-tight">What's New?</span>
              </div>
              <div className="text-base flex items-center gap-2">
                <Badge variant="secondary">
                  Version {releaseNote.version}
                </Badge>
                <span className="text-muted-foreground">{releaseNote.date}</span>
              </div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6 overflow-y-auto hide-scrollbar">
          <div className="space-y-6 pb-6">
            {/* Highlights */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">{releaseNote.title}</h3>
              <div className="space-y-2">
                {releaseNote.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">{highlight}</p>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* New Features */}
            {releaseNote.features.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <h4 className="font-semibold">New Features</h4>
                </div>
                <div className="space-y-4">
                  {releaseNote.features.map((feature, index) => (
                    <div key={index} className="space-y-1 pl-7">
                      <h5 className="font-medium text-sm">{feature.title}</h5>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Improvements */}
            {releaseNote.improvements.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold">Improvements</h4>
                  </div>
                  <ul className="space-y-2 pl-7">
                    {releaseNote.improvements.map((improvement, index) => (
                      <li key={index} className="text-sm text-muted-foreground list-disc">
                        {improvement}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {/* Bug Fixes */}
            {releaseNote.bugFixes.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Bug className="h-5 w-5 text-orange-600" />
                    <h4 className="font-semibold">Bug Fixes</h4>
                  </div>
                  <ul className="space-y-2 pl-7">
                    {releaseNote.bugFixes.map((fix, index) => (
                      <li key={index} className="text-sm text-muted-foreground list-disc">
                        {fix}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        <div className="p-6 pt-4 border-t shrink-0 flex gap-2">
          <Button onClick={() => onOpenChange(false)} className={hideViewAllLink ? "w-full" : "flex-1"}>
            Got it, thanks!
          </Button>
          {!hideViewAllLink && (
            <Button asChild variant="outline" className="flex-1">
              <a href="https://askfinz.ai/release-notes" onClick={() => onOpenChange(false)}>
                View All Release Notes
              </a>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
